import React from 'react';
import { Phone, Mail, MapPin, ExternalLink } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-heading text-primary-foreground relative overflow-hidden">
      {/* Decorative top border */}
      <div className="h-1 bg-gradient-to-r from-primary via-secondary to-primary" />
      
      <div className="max-w-7xl mx-auto container-padding py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-lg">
                <span className="text-primary-foreground font-heading text-xl font-semibold">JN</span>
              </div>
              <div>
                <span className="block text-primary-foreground/95 font-heading text-xl font-medium">JaNa Logo</span>
                <span className="block text-primary-foreground/60 text-sm">Praxis für Logopädie</span>
              </div>
            </div>
            <p className="text-primary-foreground/70 font-body text-sm leading-relaxed max-w-md mb-6">
              Professionelle logopädische Therapie für Kinder, Jugendliche und Erwachsene 
              in Schöppingen. Ich freue mich darauf, Sie bei mir begrüßen zu dürfen.
            </p>
            
            {/* Demo Notice */}
            <div className="inline-flex items-center space-x-2 bg-primary/20 border border-primary/30 rounded-lg px-4 py-2">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="text-primary-foreground/80 text-xs font-body">
                Dies ist eine Demo-Website
              </span>
            </div>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-primary-foreground/95 font-heading text-lg font-medium mb-6">
              Kontakt
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:025553210812" 
                  className="flex items-start gap-3 text-primary-foreground/70 hover:text-primary-foreground transition-colors duration-300"
                >
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="font-body text-sm">
                    <span className="block">02555 3210812</span>
                    <span className="block">0176 24076004</span>
                  </div>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@logopaedie-schoeppingen.de" 
                  className="flex items-start gap-3 text-primary-foreground/70 hover:text-primary-foreground transition-colors duration-300"
                >
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="font-body text-sm break-all">info@logopaedie-schoeppingen.de</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=Amtsstraße+2+48624+Schöppingen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-primary-foreground/70 hover:text-primary-foreground transition-colors duration-300"
                >
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div className="font-body text-sm">
                    <span className="block">Amtsstraße 2</span>
                    <span className="block">48624 Schöppingen</span>
                  </div>
                </a>
              </li>
            </ul>
          </div>

          {/* Links Column */}
          <div>
            <h4 className="text-primary-foreground/95 font-heading text-lg font-medium mb-6">
              Navigation
            </h4>
            <ul className="space-y-3">
              {[
                { href: '#praxis', label: 'Über die Praxis' },
                { href: '#ueber-mich', label: 'Über mich' },
                { href: '#leistungen', label: 'Leistungen' },
                { href: '#ablauf', label: 'Ablauf' },
                { href: '#kontakt', label: 'Kontakt' },
              ].map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.querySelector(link.href);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="text-primary-foreground/70 hover:text-primary-foreground font-body text-sm transition-colors duration-300"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            
            {/* Website Link */}
            <div className="mt-6 pt-6 border-t border-primary-foreground/10">
              <a 
                href="https://www.logopaedie-schoeppingen.de"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:text-primary-light font-body text-sm transition-colors duration-300"
              >
                <span>Offizielle Website</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-primary-foreground/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-primary-foreground/60 font-body text-sm text-center sm:text-left">
              © {currentYear} JaNa Logo – Praxis für Logopädie. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-primary-foreground/60 font-body text-sm hover:text-primary-foreground/80 cursor-pointer transition-colors duration-300">
                Impressum (Demo)
              </span>
              <span className="text-primary-foreground/60 font-body text-sm hover:text-primary-foreground/80 cursor-pointer transition-colors duration-300">
                Datenschutz (Demo)
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
